import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { PasswordInput } from '../components/PasswordInput';
import { completeLogin } from '../utils/postLoginRouting';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { identifier: email, password });
      // Two-step verification: backend issues no token until the OTP is
      // verified. Hand off to /two-factor with the challenge metadata.
      if (res.data?.requires_2fa) {
        navigate('/two-factor', {
          replace: true,
          state: {
            challengeId: res.data.challenge_id,
            email: res.data.email || email,
          },
        });
        return;
      }
      await completeLogin({
        accessToken: res.data.access_token,
        user: res.data.user,
        onLogin,
        navigate,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 40,
      }}
    >
      {/* Back arrow */}
      <button
        onClick={goBack}
        aria-label="Go back"
        data-testid="login-back-button"
        style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 20px)', left: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
      >
        <ChevronLeft size={26} color="var(--text)" strokeWidth={1.75} />
      </button>

      {/* Logo */}
      <div className="auth-fade-up" style={{ animationDelay: '0ms' }}>
        <RouteTransitionLogo width={132} height={118} dataTestId="login-shared-logo" style={{ marginTop: 56 }} />
      </div>

      {/* Heading */}
      <h1
        className="auth-fade-up"
        style={{
          fontFamily: "'Amarante', serif",
          fontSize: 30,
          fontWeight: 400,
          color: 'var(--text)',
          textAlign: 'center',
          marginTop: 14,
          marginBottom: 28,
          animationDelay: '100ms',
        }}
      >
        Welcome to Allure
      </h1>

      {/* Form card */}
      <form
        id="login-form"
        onSubmit={handleSignIn}
        className="premium-card auth-fade-up"
        style={{ width: '100%', maxWidth: 360, padding: '28px 24px', animationDelay: '200ms' }}
      >
        <div style={{ marginBottom: 18 }}>
          <label htmlFor="login-email" className="auth-label">E-mail</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="auth-email-input"
            className="auth-input"
            placeholder="you@email.com"
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="login-password" className="auth-label">Password</label>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            dataTestId="auth-password-input"
            placeholder="••••••••"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            data-testid="login-forgot-password-button"
            className="auth-link auth-link-accent"
            style={{ padding: '4px 2px' }}
          >
            Forgot password?
          </button>
        </div>
      </form>

      {/* Primary action */}
      <motion.button
        type="submit"
        form="login-form"
        disabled={loading}
        whileHover={loading ? {} : { scale: 1.01 }}
        whileTap={loading ? {} : { scale: 0.99 }}
        className="auth-primary-btn auth-fade-up"
        data-testid="auth-submit-button"
        style={{ maxWidth: 360, marginTop: 24, animationDelay: '300ms' }}
      >
        {loading ? 'Signing in…' : 'Sign In'}
      </motion.button>

      {/* Divider */}
      <div className="auth-divider auth-fade-up" style={{ maxWidth: 360, margin: '22px 0', animationDelay: '380ms' }}>
        or
      </div>

      {/* Continue with Google */}
      <GoogleSignInButton
        onLogin={onLogin}
        dataTestId="login-google-button"
        style={{ maxWidth: 360, animationDelay: '420ms' }}
      />

      {/* Create account */}
      <p
        className="auth-fade-up"
        style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: '#666666', marginTop: 28, animationDelay: '480ms' }}
      >
        Don&apos;t have an account?{' '}
        <button
          type="button"
          onClick={() => navigate('/register')}
          data-testid="login-create-account-button"
          className="auth-link auth-link-accent"
        >
          Create Account
        </button>
      </p>
    </motion.div>
  );
}
